import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { presetForOpportunity } from '../data/oppPresets';
import { api } from '../services/api';
import OpportunityRadar from '../components/OpportunityRadar';
import { SECTORS } from '../data/sectors';
import './Onboarding.css';

/**
 * 🚀 Onboarding « première victoire » (Lot C — mockup validé).
 * 4 étapes + mission profil digital. Doctrine : simple, victoire rapide,
 * anticipation — le génie propose, l'utilisateur valide.
 */

// Liste riche : le user tape, on suggère — s'il écrit un métier absent,
// son mot devient LA référence (pas de case « Autre » fourre-tout).
// Liste des métiers : source unique partagée (voir src/data/sectors.js)
const CANTONS = [
  'Valais', 'Vaud', 'Genève', 'Fribourg', 'Neuchâtel', 'Jura', 'Berne',
  'Zurich', 'Bâle', 'Lucerne', 'Tessin', 'Autre canton',
];

// Messages prêts par type d'opportunité (le génie propose, l'utilisateur valide)
const ACTION_PRESETS = {
  dormants: {
    objective: 'Réactiver mes clients inactifs',
    subject: '{{prénom}}, on pense à toi ✨',
    body: (company, owner) => `Bonjour {{prénom}},\n\nÇa fait un moment qu'on ne t'a pas vu·e — et franchement, tu nous manques !\nPour te redonner envie de passer, je t'ai réservé une petite attention valable ce mois-ci.\n\nÀ très vite,\n${owner || `L'équipe ${company}`}`,
  },
  birthdays: {
    objective: 'Fidéliser mes clients',
    subject: 'Un joyeux anniversaire en avance, {{prénom}} 🎂',
    body: (company, owner) => `Bonjour {{prénom}},\n\nTon anniversaire approche — et je voulais être parmi les premiers à y penser !\nPasse nous voir ce mois-ci : une attention t'attend pour marquer le coup.\n\n${owner || `L'équipe ${company}`} 🎉`,
  },
  clickers: {
    objective: 'Convertir mes prospects intéressés',
    subject: '{{prénom}}, tu y as jeté un œil… 👀',
    body: (company, owner) => `Bonjour {{prénom}},\n\nJ'ai vu que notre dernière offre a retenu ton attention — bonne nouvelle, elle est encore valable !\nPasse quand tu veux, je t'ai gardé une place.\n\nÀ très vite,\n${owner || `L'équipe ${company}`}`,
  },
  newcomers: {
    objective: 'Fidéliser mes clients',
    subject: 'Bienvenue chez nous, {{prénom}} 💛',
    body: (company, owner) => `Bonjour {{prénom}},\n\nMerci pour ta première visite — j'espère qu'elle t'a plu !\nPour la prochaine, une petite surprise de bienvenue t'attend.\n\nAu plaisir de te revoir,\n${owner || `L'équipe ${company}`}`,
  },
};

const ACTIONS_PRESETS_SAFE = (key) => ACTION_PRESETS[key] || ACTION_PRESETS.dormants;

export default function Onboarding() {
  const navigate = useNavigate();
  const { tenant, user, contacts, companyProfile, setCompanyProfile, addNotification } = useApp();
  const [step, setStep] = useState(1);

  // ── Étape 1 : profil express ──
  const [form, setForm] = useState({
    sector: companyProfile?.sector || '',
    canton: companyProfile?.geographic_zone || '',
    basket: companyProfile?.avg_client_value || '',
    tone: 'friendly',
    website: companyProfile?.website || '',
  });
  const [saving, setSaving] = useState(false);
  const [errMsg, setErrMsg] = useState('');
  const [basketSkip, setBasketSkip] = useState(false);
  const [sectorFocus, setSectorFocus] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const step1Ok = form.sector.trim().length >= 3 && form.canton && (basketSkip || form.basket);

  // Suggestions secteur : filtre fluide, insensible aux accents
  const norm = (t) => (t || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const sectorSuggestions = useMemo(() => {
    const q = norm(form.sector.trim());
    if (!q) return SECTORS;
    return SECTORS.filter(sec => norm(sec).includes(q));
  }, [form.sector]);
  const sectorIsKnown = SECTORS.some(sec => norm(sec) === norm(form.sector.trim()));

  const saveProfile = async () => {
    if (!step1Ok) { setErrMsg('Encore 1-2 réponses et on y est 😊'); return; }
    setSaving(true);
    setErrMsg('');
    // Mise en forme propre du secteur saisi (majuscule, espaces)
    const cleanSector = form.sector.trim().replace(/\s+/g, ' ');
    const sector = cleanSector.charAt(0).toUpperCase() + cleanSector.slice(1);
    // IMPORTANT : n'envoyer QUE ces champs — le backend fusionne avec l'existant.
    // (Envoyer tout le profil local faisait échouer la validation en silence.)
    const payload = {
      sector,
      geographic_zone: form.canton,
      communication_tone: form.tone,
      basket_disabled: basketSkip,
      ...(basketSkip ? {} : { avg_client_value: String(form.basket) }),
      ...(form.website ? { website: form.website.trim() } : {}),
    };
    try {
      await api.saveCompany(payload);
      setCompanyProfile({ ...(companyProfile || {}), ...payload });
      api.saveOnboardingState({ profile: true }).catch(() => {});
      // Le génie analyse le site en arrière-plan pour pré-remplir le reste
      if (form.website) {
        api.analyzeWebsite(form.website.trim()).then(res => {
          if (res && typeof res === 'object' && !res.error) {
            api.saveCompany(res).catch(() => {});
          }
        }).catch(() => {});
      }
      setStep(2);
    } catch (e) {
      setErrMsg(e.message || "L'enregistrement a échoué — réessaie.");
    } finally {
      setSaving(false);
    }
  };

  // ── Étape 2 : contacts (auto-détection si déjà importés) ──
  const hasContacts = contacts.length > 0;
  useEffect(() => {
    if (hasContacts) api.saveOnboardingState({ contacts: true }).catch(() => {});
  }, [hasContacts]);

  // ── Étape 4 : première action ──
  const [opps, setOpps] = useState(null);
  useEffect(() => {
    if (step >= 3 && opps === null) {
      api.getOpportunities().then(setOpps).catch(() => setOpps({ opportunities: [] }));
    }
  }, [step, opps]);

  // Première action MANUELLE = la meilleure opportunité qui n'est pas déjà
  // gérée automatiquement (les vœux auto n'ont pas besoin de lui).
  const autoBirthday = (opps?.opportunities ?? []).find(o => o.key === 'birthdays' && o.auto) ?? null;
  const topOpp = (opps?.opportunities ?? []).find(o => !(o.key === 'birthdays' && o.auto)) ?? null;
  const preset = useMemo(() => {
    if (!topOpp) return null;
    const company = tenant?.name || 'notre équipe';
    // Signature humaine : prénom du patron + commerce (doctrine mode humain)
    const owner = user?.name ? `${user.name.split(' ')[0]} — ${company}` : null;
    const tone = form.tone === 'formal' ? 'vouvoiement' : 'tutoiement';
    // Brique UNIQUE des presets radar — même campagne préconstruite que sur l'Accueil
    const built = presetForOpportunity(topOpp, { company, owner, tone });
    if (built) return built.preset;
    const t = ACTIONS_PRESETS_SAFE(topOpp.key);
    return {
      name: topOpp.title,
      objective: t.objective,
      tone,
      subject: t.subject,
      body: t.body(company, owner),
    };
  }, [topOpp, tenant, form.tone]);

  const launchFirstAction = () => {
    api.saveOnboardingState({ radar_seen: true, first_send: true, completed: true }).catch(() => {});
    navigate('/campaigns/new', { state: { preset, startStep: 2 } });
  };

  const finish = (target = '/dashboard') => {
    api.saveOnboardingState({ completed: true }).catch(() => {});
    navigate(target);
  };

  // ── Mission profil (étape 5) ──
  const cp = companyProfile || {};
  const missionItems = [
    { icon: '🌐', label: 'Site web',            ok: !!cp.website },
    { icon: '📘', label: 'Facebook',            ok: !!cp.facebook_url },
    { icon: '📸', label: 'Instagram',           ok: !!cp.instagram_url },
    { icon: '💼', label: 'LinkedIn',            ok: !!cp.linkedin_url },
    { icon: '📍', label: 'Google Business',     ok: !!cp.google_my_business_url || cp.gmb_has_page === true },
    { icon: '🎨', label: 'Logo & couleurs',     ok: !!cp.logo_url || !!cp.brand_colors },
    { icon: '🛍', label: 'Services & offre',    ok: !!cp.services },
    { icon: '⭐', label: 'Ce qui te rend unique', ok: !!cp.unique_value_proposition || !!cp.unique_selling_point },
  ];
  const missionPct = Math.round(missionItems.filter(i => i.ok).length / missionItems.length * 100);

  const StepDots = () => (
    <div className="ob-prog">
      {[1, 2, 3, 4].map(n => <i key={n} className={step >= n ? 'on' : ''} />)}
    </div>
  );

  return (
    <div className="ob-bg">
      <div className="ob-frame">
        {step <= 4 && <StepDots />}

        {/* ── ÉTAPE 1 ── */}
        {step === 1 && (
          <div className="ob-screen">
            <div className="ob-tag">Étape 1 / 4 · 60 secondes</div>
            <h1>🏢 Parle-nous de ton commerce</h1>
            <p className="ob-sub">5 réponses — c'est tout ce qu'il faut au génie pour travailler avec tes mots et tes chiffres.</p>
            <div className="ob-fields">
              <div className="ob-f ob-sector">
                <label>Ton secteur</label>
                <input
                  type="text"
                  placeholder="Commence à taper… (coiffure, garage, marketing…)"
                  value={form.sector}
                  onChange={e => set('sector', e.target.value)}
                  onFocus={() => setSectorFocus(true)}
                  onBlur={() => setTimeout(() => setSectorFocus(false), 180)}
                />
                {sectorFocus && (sectorSuggestions.length > 0 || form.sector.trim().length >= 3) && (
                  <div className="ob-sector-sug">
                    {sectorSuggestions.map(sec => (
                      <button key={sec} type="button"
                        onMouseDown={() => { set('sector', sec); setSectorFocus(false); }}>
                        {sec}
                      </button>
                    ))}
                    {!sectorIsKnown && form.sector.trim().length >= 3 && (
                      <button type="button" className="ob-sector-custom"
                        onMouseDown={() => setSectorFocus(false)}>
                        ➕ Utiliser « {form.sector.trim()} » — ce sera ta référence
                      </button>
                    )}
                  </div>
                )}
                {form.sector && sectorIsKnown && <span className="ob-hint">✓ Secteur reconnu</span>}
              </div>
              <div className="ob-f">
                <label>Ton canton</label>
                <select value={form.canton} onChange={e => set('canton', e.target.value)}>
                  <option value="">Choisis…</option>
                  {CANTONS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="ob-f">
                <label>Panier moyen d'un client (CHF)</label>
                <input type="number" min="1" placeholder="80" value={form.basket}
                  disabled={basketSkip}
                  onChange={e => set('basket', e.target.value)} />
                <label className="ob-check">
                  <input type="checkbox" checked={basketSkip} onChange={e => setBasketSkip(e.target.checked)} />
                  🤷 Difficile à dire — ne pas chiffrer en francs
                </label>
                <span className="ob-hint">
                  {basketSkip
                    ? 'Le radar comptera tes clients (pas de montants) — modifiable plus tard'
                    : 'Une estimation grossière suffit — sert à chiffrer tes opportunités'}
                </span>
              </div>
              <div className="ob-f">
                <label>Tu dis « tu » ou « vous » à tes clients ?</label>
                <div className="ob-choices">
                  <button className={form.tone === 'friendly' ? 'sel' : ''} onClick={() => set('tone', 'friendly')}>Tu 😊</button>
                  <button className={form.tone === 'formal' ? 'sel' : ''} onClick={() => set('tone', 'formal')}>Vous 🤝</button>
                </div>
              </div>
              <div className="ob-f ob-full">
                <label>Ton site ou ta page Facebook <span className="ob-opt">(facultatif)</span></label>
                <input type="text" placeholder="www.mon-commerce.ch" value={form.website} onChange={e => set('website', e.target.value)} />
                <span className="ob-hint">✨ Le génie analysera la page pour pré-remplir le reste tout seul</span>
              </div>
            </div>
            {errMsg && <div className="ob-err">⚠️ {errMsg}</div>}
            <div className="ob-foot">
              <span />
              <button className="ob-next" disabled={!step1Ok || saving} onClick={saveProfile}>
                {saving ? 'Enregistrement…' : 'Continuer →'}
              </button>
            </div>
          </div>
        )}

        {/* ── ÉTAPE 2 ── */}
        {step === 2 && (
          <div className="ob-screen">
            <div className="ob-tag">Étape 2 / 4</div>
            <h1>📥 Tes contacts</h1>
            <p className="ob-sub">Un fichier Excel suffit — Appee reconnaît les colonnes tout seul.</p>

            {hasContacts ? (
              <div className="ob-diag">
                <span>✅ {contacts.length} contacts dans ta base</span>
                <span>🎂 {contacts.filter(c => c.birth_date).length} avec anniversaire</span>
                {contacts.filter(c => !c.email).length > 0 && (
                  <span className="warn">✉️ {contacts.filter(c => !c.email).length} sans e-mail</span>
                )}
              </div>
            ) : (
              <div className="ob-cards3">
                <button className="ob-c3 ob-c3-main" onClick={() => navigate('/imports', { state: { returnTo: '/onboarding' } })}>
                  <span className="ico">📄</span>
                  <h3>Importer un fichier</h3>
                  <p>Excel ou CSV — mapping automatique, doublons gérés</p>
                  <span className="tag">Recommandé</span>
                </button>
                <button className="ob-c3" onClick={() => navigate('/contacts/new', { state: { returnTo: '/onboarding' } })}>
                  <span className="ico">✍️</span>
                  <h3>Ajouter à la main</h3>
                  <p>Quelques contacts pour commencer, le fichier viendra plus tard</p>
                </button>
                <button className="ob-c3" onClick={() => setStep(3)}>
                  <span className="ico">👀</span>
                  <h3>Voir d'abord</h3>
                  <p>Découvre l'app, tu importeras ensuite</p>
                </button>
              </div>
            )}

            <div className="ob-foot">
              <button className="ob-skip" onClick={() => setStep(3)}>Passer pour l'instant</button>
              <button className="ob-next" onClick={() => setStep(3)}>Continuer →</button>
            </div>
          </div>
        )}

        {/* ── ÉTAPE 3 : le radar réel ── */}
        {step === 3 && (
          <div className="ob-screen">
            <div className="ob-tag">Étape 3 / 4 · le moment magique</div>
            <h1>📡 Ton radar s'allume…</h1>
            <p className="ob-sub">
              {hasContacts
                ? 'Appee vient d\'analyser TA base — voilà ce qui dort dedans.'
                : 'Voilà à quoi ressemblera ton radar dès que tes contacts seront là.'}
            </p>
            {hasContacts && (() => {
              // 🔎 Découvertes concrètes sur SA base — le « purée, j'avais pas vu ça »
              const nEmail = contacts.filter(c => c.email).length;
              const nPhone = contacts.filter(c => c.phone).length;
              const nBday  = contacts.filter(c => c.birth_date).length;
              const nMute  = contacts.filter(c => !c.email && !c.phone).length;
              return (
                <div className="ob-discover">
                  <span className="ob-discover-chip">📧 <b>{nEmail}</b> joignables par e-mail</span>
                  <span className="ob-discover-chip">📱 <b>{nPhone}</b> par SMS</span>
                  <span className="ob-discover-chip">🎂 <b>{nBday}</b> anniversaires connus</span>
                  {nMute > 0 && (
                    <span className="ob-discover-chip warn">🕳 <b>{nMute}</b> injoignables (ni e-mail ni tél) — à compléter</span>
                  )}
                </div>
              );
            })()}
            {hasContacts ? <OpportunityRadar /> : (
              <div className="ob-demo-radar">
                <div className="ob-demo-badge">👀 Aperçu avec des données d'exemple — tes vrais chiffres apparaîtront ici</div>
                <div className="ob-demo-rows">
                  <div className="ob-demo-row">
                    <span>💤 86 clients silencieux depuis 6 mois</span>
                    <i style={{ width: '88%' }} /><b>{basketSkip ? '86 clients' : '~2 580 CHF'}</b>
                  </div>
                  <div className="ob-demo-row">
                    <span>🎂 9 anniversaires dans les 30 jours</span>
                    <i style={{ width: '34%' }} /><b>{basketSkip ? '9 clients' : '~540 CHF'}</b>
                  </div>
                  <div className="ob-demo-row">
                    <span>🏆 12 clients en or à choyer</span>
                    <i style={{ width: '52%' }} /><b>{basketSkip ? '12 clients' : '~1 150 CHF'}</b>
                  </div>
                </div>
                <button className="ob-demo-cta" onClick={() => navigate('/imports', { state: { returnTo: '/onboarding' } })}>
                  📥 Importer mes contacts pour voir MES chiffres
                </button>
              </div>
            )}
            <div className="ob-foot">
              <span />
              <button className="ob-next" onClick={() => { api.saveOnboardingState({ radar_seen: true }).catch(() => {}); setStep(4); }}>
                {hasContacts ? 'Voir ma première action →' : 'Continuer →'}
              </button>
            </div>
          </div>
        )}

        {/* ── ÉTAPE 4 : première action ── */}
        {step === 4 && (
          <div className="ob-screen">
            <div className="ob-tag">Étape 4 / 4 · rien à écrire</div>
            {/* Les vœux automatiques ne sont PAS une action à faire : on le dit,
                et la « première action » proposée est la meilleure opportunité MANUELLE. */}
            {autoBirthday && (
              <div className="ob-auto-banner">
                🎂 <b>{autoBirthday.count} anniversaire{autoBirthday.count > 1 ? 's' : ''}</b> dans les 30 jours ?
                Déjà réglé : les vœux partiront <b>automatiquement</b> le jour J à 7h. Toi, tu n'as rien à faire.
              </div>
            )}
            {preset ? (
              <>
                <h1>🚀 Ta première action est prête</h1>
                <p className="ob-sub">Le génie a choisi la plus rentable et a déjà tout rédigé — tu relis, tu valides.</p>
                <div className="ob-action">
                  <div className="ob-a-head">
                    {topOpp.icon} {topOpp.title}
                    {topOpp.potential != null && <span className="ob-a-chf">~{topOpp.potential.toLocaleString('fr-CH')} CHF</span>}
                  </div>
                  <div className="ob-a-msg">
                    <b>Objet :</b> {preset.subject}
                    <br /><br />
                    {preset.body.split('\n').map((l, i) => <React.Fragment key={i}>{l}<br /></React.Fragment>)}
                  </div>
                  <p className="ob-a-note">✏️ Tu pourras tout modifier avant l'envoi · 📧 Envoyé via Appee, aucune configuration · les réponses arrivent sur TON adresse</p>
                </div>
                <div className="ob-foot">
                  <button className="ob-skip" onClick={() => setStep(5)}>Plus tard</button>
                  <button className="ob-next" onClick={launchFirstAction}>✅ Préparer et envoyer</button>
                </div>
              </>
            ) : autoBirthday ? (
              <>
                <h1>😌 Rien à préparer — je m'occupe de tout</h1>
                <p className="ob-sub">
                  Ta meilleure opportunité du moment (les anniversaires) est déjà automatisée.
                  Dès qu'une action rentable demandera ta validation, le génie te la proposera sur ton Accueil.
                </p>
                <div className="ob-foot">
                  <button className="ob-skip" onClick={() => navigate('/birthdays')}>Voir le programme des vœux</button>
                  <button className="ob-next" onClick={() => setStep(5)}>Terminer →</button>
                </div>
              </>
            ) : (
              <>
                <h1>🚀 Ta première action t'attend</h1>
                <p className="ob-sub">Dès que tes contacts seront là, le génie choisira l'action la plus rentable et la rédigera pour toi — et les anniversaires partiront automatiquement chaque matin, sans que tu y penses.</p>
                <div className="ob-cards3">
                  <button className="ob-c3 ob-c3-main" onClick={() => navigate('/imports', { state: { returnTo: '/onboarding' } })}>
                    <span className="ico">📥</span>
                    <h3>Importer maintenant</h3>
                    <p>2 minutes — et ta première action sera prête juste après</p>
                    <span className="tag">Recommandé</span>
                  </button>
                  <button className="ob-c3" onClick={() => setStep(5)}>
                    <span className="ico">🕐</span>
                    <h3>Plus tard</h3>
                    <p>La checklist sur ton Accueil te le rappellera</p>
                  </button>
                </div>
                <div className="ob-foot">
                  <span />
                  <button className="ob-next" onClick={() => setStep(5)}>Continuer →</button>
                </div>
              </>
            )}
          </div>
        )}

        {/* ── ÉTAPE 5 : victoire + mission profil digital ── */}
        {step === 5 && (
          <div className="ob-screen">
            <div className="ob-done">
              <span className="big">🎉</span>
              <h1>C'est parti ! Appee travaille pour toi.</h1>
              <p className="ob-sub">Anniversaires automatiques chaque matin à 7h · rendez-vous lundi 8h pour tes opportunités.</p>
            </div>

            <div className="ob-action">
              <div className="ob-a-head">
                🎯 Ta prochaine mission : ton profil digital
                <span className="ob-a-pct">{missionPct}% complété</span>
              </div>
              <div className="ob-gauge"><i style={{ width: `${Math.max(missionPct, 6)}%` }} /></div>
              <p className="ob-mission-txt">
                Plus le génie te connaît, plus il voit loin. Complète ton profil et débloque
                <b> l'audit complet de ton image digitale</b> — site, réseaux, visibilité — avec
                les conseils qui vont avec, adaptés à TON commerce.
              </p>
              <div className="ob-chips">
                {missionItems.map(m => (
                  <span key={m.label} className={m.ok ? 'ok' : ''}>{m.icon} {m.label}{m.ok ? ' ✓' : ''}</span>
                ))}
              </div>
              <p className="ob-a-note">✨ Colle simplement tes liens — le génie analyse tes pages et remplit les détails.
              À 100% : <b>🏅 audit d'image digitale offert</b>.</p>
            </div>

            <div className="ob-foot">
              <button className="ob-skip" onClick={() => finish('/dashboard')}>Plus tard — la mission t'attend sur ton Accueil</button>
              <button className="ob-next" onClick={() => finish('/settings?tab=entreprise')}>🎯 Compléter mon profil (5 min)</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
